import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import PageHeader from "../../../Ui_Components/PageHeader";
import StepIndicator from "../../../Team/AddNutritionist/components/StepIndicator";
import { ToastContainer, useToast } from "../../../Ui_Components/ToastMessage";
import { Button } from "../../../Ui_Components/Button";
import DetailsNutritionStep, {
  DuplicateNameInfo,
} from "./steps/DetailsNutritionStep";
import UnitConversionStep from "./steps/UnitConversionStep";
import type { IngredientDetail } from "../types";
import { mapApiToIngredientDetail } from "../ingredientApiMapper";
import {
  AddIngredientFormData,
  DetailsNutritionErrors,
  MICRO_NUTRIENT_BACKEND_KEY,
  MICRO_NUTRIENT_FIELDS,
  UnitConversionErrors,
  buildFormFromIngredient,
  initialDetailsNutrition,
  initialMicroNutrients,
  initialUnitConversion,
  validateDetailsNutrition,
  validateUnitConversion,
} from "./types";
import {
  IngredientUnit,
  createIngredient,
  getIngredientDetails,
  getUnitsList,
  updateIngredient,
} from "../../../../services/ingredients";
import { calculateCPFRatio } from "../../../Plans/Meal_Creation/CPFCalculator";

const STEPS = [
  { number: 1, label: "Details & nutrition" },
  { number: 2, label: "Unit conversion" },
];

// Block step-1 progression when the entered macros produce a negative
// component in the C:P:F ratio (fat + protein calories exceeding the stated
// total calories, which pushes carb % below zero). Returns null when the
// values are consistent or when calories haven't been entered yet.
const getNegativeCpfError = (details: {
  calories: string;
  adjProtein: string;
  fat: string;
  carbs: string;
}): string | null => {
  const cal = parseFloat(details.calories) || 0;
  if (cal <= 0) return null;
  const protein = parseFloat(details.adjProtein) || 0;
  const fat = parseFloat(details.fat) || 0;
  const carbs = parseFloat(details.carbs) || 0;
  const { ratio } = calculateCPFRatio(carbs, protein, fat, cal);
  if (Number(ratio.c) < 0 || Number(ratio.p) < 0 || Number(ratio.f) < 0) {
    return "C:P:F value cannot be negative. Fat and protein together exceed the total calories.";
  }
  return null;
};

const AddIngredientPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const { toasts, removeToast, error: showError } = useToast();

  // Edit mode is triggered by the /:id/edit route. When the user enters
  // edit from the View page we get the ingredient via navigation state
  // (no redundant fetch). When entering directly from the list, refresh,
  // or a bookmarked URL there is no state — we fetch the detail record
  // from the API and populate the form once it lands.
  const stateIngredient = (location.state as { ingredient?: IngredientDetail } | null)
    ?.ingredient;
  const isEditMode = Boolean(id);
  const [editIngredient, setEditIngredient] = useState<
    IngredientDetail | undefined
  >(stateIngredient);
  const [loadingIngredient, setLoadingIngredient] = useState(
    isEditMode && !stateIngredient,
  );
  const [ingredientLoadError, setIngredientLoadError] = useState<string | null>(
    null,
  );

  const [currentStep, setCurrentStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  // Units master list is fetched once so Step 2's conversion rows (which only
  // carry unit_type strings) can be resolved to the unit_id values the
  // CREATE_INGREDIENT payload expects.
  const [unitsList, setUnitsList] = useState<IngredientUnit[]>([]);

  const [formData, setFormData] = useState<AddIngredientFormData>(() => {
    if (stateIngredient) return buildFormFromIngredient(stateIngredient);
    return {
      details: initialDetailsNutrition,
      conversion: initialUnitConversion,
    };
  });

  const [step1Errors, setStep1Errors] = useState<DetailsNutritionErrors>({});
  const [step2Errors, setStep2Errors] = useState<UnitConversionErrors>({});
  // Duplicate-name state bubbled up from Step 1. When set, "Save & next"
  // is blocked so the user can't submit an already-existing ingredient.
  const [duplicateName, setDuplicateName] = useState<DuplicateNameInfo | null>(
    null,
  );

  // Fetch the ingredient from the API when in edit mode without a preloaded
  // state (i.e. entered via the list "Edit" action or a direct URL). Skipped
  // when we already have the ingredient from navigation state.
  useEffect(() => {
    if (!isEditMode || !id || stateIngredient) return;
    let cancelled = false;
    setLoadingIngredient(true);
    setIngredientLoadError(null);
    (async () => {
      try {
        const api = await getIngredientDetails(id);
        if (cancelled) return;
        const isSuccess =
          api?.success === true ||
          api?.success === "1" ||
          api?.success === 1 ||
          api?.success === "true";
        if (!isSuccess || !api?.food_data) {
          setIngredientLoadError(api?.message || "Failed to load ingredient");
          setLoadingIngredient(false);
          return;
        }
        const mapped = mapApiToIngredientDetail(api.food_data);
        setEditIngredient(mapped);
        setFormData(buildFormFromIngredient(mapped));
        setLoadingIngredient(false);
      } catch (err) {
        if (cancelled) return;
        setIngredientLoadError(
          err instanceof Error ? err.message : "Failed to load ingredient",
        );
        setLoadingIngredient(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, isEditMode, stateIngredient]);

  // If the user navigates directly between edit URLs, or React Router reuses
  // the same instance across `/add` → `/:id/edit`, the initial useState
  // wouldn't re-run. Re-sync the form whenever the edited ingredient changes.
  useEffect(() => {
    if (editIngredient) {
      setFormData(buildFormFromIngredient(editIngredient));
      setCurrentStep(1);
      setStep1Errors({});
      setStep2Errors({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editIngredient?.id]);

  // Pre-fetch the units master list so submitting doesn't have to wait on it.
  // Failure isn't fatal for the form itself — surface it only if the user
  // reaches submit without a resolved list.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await getUnitsList();
        if (!cancelled) setUnitsList(list);
      } catch (err) {
        console.error("[AddIngredientPage] units fetch failed", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleCancel = () => {
    if (isEditMode && id) navigate(`/food-library/ingredient/${id}`);
    else navigate("/food-library/ingredient");
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep((s) => s - 1);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const apiType =
        formData.details.type === "veg"
          ? "VEG"
          : formData.details.type === "nonveg"
            ? "NON_VEG"
            : null;
      if (!apiType) throw new Error("Invalid diet type");

      // Resolve each conversion row's unit_type → unit_id. Rows the user
      // hasn't finished filling (blank unit or grams) are skipped. Locked
      // base rows (gm/oz or ml/fl oz) are also skipped — they're UI-only
      // anchors driven by the frontend; the backend owns those base
      // conversions and would duplicate them if we sent them here.
      const unitByType = new Map(
        unitsList.map((u) => [u.unit_type, u.unit_id]),
      );
      const unitPayload = formData.conversion.rows
        .filter((r) => !r.locked && r.unit && r.grams)
        .map((r) => {
          const unitId = unitByType.get(r.unit);
          if (unitId === undefined) {
            throw new Error(`Unknown unit "${r.unit}"`);
          }
          return { unit_id: unitId, measure_gram: Number(r.grams) };
        });

      // Micro-nutrients are shipped keyed by backend snake_case names. Empty
      // fields are passed through as "" so the payload always carries all 29
      // fields — the backend distinguishes "not set" vs. a numeric value on
      // its own side.
      const microSource = formData.details.micro ?? initialMicroNutrients;
      const microPayload: Record<string, string> = {};
      MICRO_NUTRIENT_FIELDS.forEach((key) => {
        microPayload[MICRO_NUTRIENT_BACKEND_KEY[key]] = microSource[key] ?? "";
      });

      const payload = {
        item_name: formData.details.name.trim(),
        units: formData.details.unit,
        base_quantity: Number(formData.details.baseQty),
        category: formData.details.category,
        type: apiType,
        cals: Number(formData.details.calories),
        AAFAdj_protein: Number(formData.details.adjProtein),
        gross_protein: Number(formData.details.grossProtein),
        total_fat: Number(formData.details.fat),
        carbs: formData.details.carbs
          ? Number(formData.details.carbs)
          : undefined,
        net_carbs: Number(formData.details.netCarbs),
        GL: Number(formData.details.gl),
        fibre:
          formData.details.fiber !== ""
            ? Number(formData.details.fiber)
            : ("" as const),
        tags: formData.details.tags,
        unit: unitPayload,
        micro: microPayload,
        picture: formData.details.photo,
      } as const;

      const api =
        isEditMode && id
          ? await updateIngredient(id, payload)
          : await createIngredient(payload);

      const isSuccess =
        api?.success === true ||
        api?.success === "1" ||
        api?.success === 1 ||
        api?.success === "true";
      if (!isSuccess) {
        throw new Error(
          api?.message ||
            (isEditMode
              ? "Failed to update ingredient"
              : "Failed to add ingredient"),
        );
      }

      // Defer the success toast to the destination page so it survives the
      // redirect (this component unmounts before the toast can render here).
      navigate(
        isEditMode && id ? `/food-library/ingredient/${id}` : "/food-library/ingredient",
        {
          state: {
            showSuccessToast: true,
            toastMessage:
              api?.message ||
              (isEditMode
                ? "Ingredient updated successfully"
                : "Ingredient added successfully"),
          },
        },
      );
    } catch (err) {
      showError(
        isEditMode ? "Failed to update ingredient" : "Failed to add ingredient",
        err instanceof Error ? err.message : undefined,
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleNext = () => {
    if (currentStep === 1) {
      const errs = validateDetailsNutrition(formData.details);
      if (Object.keys(errs).length > 0) {
        setStep1Errors(errs);
        showError("Please fix the highlighted fields");
        return;
      }
      if (duplicateName) {
        showError(
          `${duplicateName.matchedItemName} already exists`,
          "Choose a different ingredient name to continue.",
        );
        return;
      }
      const cpfError = getNegativeCpfError(formData.details);
      if (cpfError) {
        showError("Invalid macro nutrients", cpfError);
        return;
      }
      setStep1Errors({});
      setCurrentStep(2);
      return;
    }

    if (currentStep === 2) {
      const errs = validateUnitConversion(formData.conversion);
      if (errs.rows && Object.keys(errs.rows).length > 0) {
        setStep2Errors(errs);
        showError("Please fix the highlighted fields");
        return;
      }
      setStep2Errors({});
      handleSubmit();
    }
  };

  const backButton = currentStep > 1 && (
    <button
      type="button"
      onClick={handleBack}
      disabled={submitting}
      className="flex items-center gap-2 text-[#0DB5B5] text-sm 3xl:text-base font-medium font-inter hover:text-[#0CA0A0] transition-colors disabled:opacity-50"
    >
      <svg
        className="w-4 h-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M15 19l-7-7 7-7"
        />
      </svg>
      Back
    </button>
  );

  const cancelButton = (
    <Button
      variant="secondary"
      size="md"
      onClick={handleCancel}
      disabled={submitting}
    >
      Cancel
    </Button>
  );

  // The primary button looks inactive until the current step's required
  // fields are filled, but stays clickable so a click still surfaces the
  // per-field validation errors (matching the previous behavior).
  const isStep1Complete =
    Object.keys(validateDetailsNutrition(formData.details)).length === 0 &&
    !duplicateName;
  const isStep2Complete = !validateUnitConversion(formData.conversion).rows;
  const currentStepComplete =
    currentStep === 1 ? isStep1Complete : isStep2Complete;
  const inactiveClass = !currentStepComplete
    ? "opacity-50 cursor-not-allowed"
    : "";

  const rightButton =
    currentStep === 2 ? (
      <Button
        variant="primary"
        size="md"
        onClick={handleNext}
        loading={submitting}
        className={inactiveClass}
      >
        {submitting
          ? isEditMode
            ? "Updating..."
            : "Saving..."
          : isEditMode
            ? "Update"
            : "Save"}
      </Button>
    ) : (
      <Button
        variant="primary"
        size="md"
        onClick={handleNext}
        className={inactiveClass}
      >
        Save &amp; next
      </Button>
    );

  return (
    <div className="h-screen flex flex-col bg-[#F7F7FA]">
      <PageHeader
        title={isEditMode ? "Edit ingredient" : "Add new ingredient"}
        breadcrumb={
          isEditMode
            ? `Ingredients > ${editIngredient?.name ?? "Edit ingredient"}`
            : "Ingredients > Add new ingredient"
        }
        breadcrumbLinks={["/food-library/ingredient"]}
      />

      <StepIndicator currentStep={currentStep} steps={STEPS} />

      {loadingIngredient || ingredientLoadError ? (
        <div className="flex-1 flex items-center justify-center">
          <p
            className={`font-inter ${
              ingredientLoadError ? "text-red-500" : "text-gray-500"
            }`}
          >
            {loadingIngredient
              ? "Loading ingredient..."
              : ingredientLoadError}
          </p>
        </div>
      ) : (
      <div className="flex-1 overflow-y-auto px-4 sm:px-6 lg:px-8 pb-4 custom-scrollbar-overlay">
        {/* Both steps stay mounted so entered data survives step navigation
            without a remount; inactive step is hidden via CSS. */}
        <div className={currentStep === 1 ? "" : "hidden"}>
          <DetailsNutritionStep
            data={formData.details}
            onChange={(details) => {
              setFormData((f) => ({ ...f, details }));
              if (Object.keys(step1Errors).length > 0)
                setStep1Errors(validateDetailsNutrition(details));
            }}
            errors={step1Errors}
            onError={showError}
            originalName={editIngredient?.name}
            currentFoodId={
              isEditMode && id !== undefined ? Number(id) : undefined
            }
            onDuplicateChange={setDuplicateName}
          />
        </div>
        <div className={currentStep === 2 ? "" : "hidden"}>
          <UnitConversionStep
            data={formData.conversion}
            unit={formData.details.unit}
            onChange={(conversion) => {
              setFormData((f) => ({ ...f, conversion }));
              if (step2Errors.rows && Object.keys(step2Errors.rows).length > 0)
                setStep2Errors(validateUnitConversion(conversion));
            }}
            errors={step2Errors}
          />
        </div>
      </div>
      )}

      {/* Sticky footer */}
      <div className="flex-shrink-0 bg-white border-t border-[#E8E8E8] px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between gap-3">
        <div>{backButton}</div>
        <div className="flex items-center gap-3">
          {cancelButton}
          {rightButton}
        </div>
      </div>

      <ToastContainer toasts={toasts} onClose={removeToast} />
    </div>
  );
};

export default AddIngredientPage;
